/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { FC, useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import {
  Button,
  Divider,
  ColorPicker,
  Grid,
  GridCell,
  Input,
  langs,
  PageSection,
  Spacer,
  Stack,
  IconButton,
  Icon,
  TextSubtitle,
  TextBody,
  RadioGroup,
  Radio,
  Image,
  Filepond,
  EntityTypes,
  PortalDestination,
  Snackbar,
  TabPanel,
} from '@pypestream/design-system';
import PypeLogo from '@pypestream/design-system/assets/pypestream-logo-black.svg?react';
import {
  buildBrandMomentUploadUrl,
  buildLogoUploadUrl,
  BrandMomentUploadContextField,
  capitalizeWords,
  createDeferredFileUpload,
  createSaveSettlement,
  getPlatformLogo,
  LogoUploadContextField,
  resolveBrandMomentUrl,
  resolveMaskingAssignment,
  toChannelEnv,
  uploadBrandMomentFile,
  uploadLogoFile,
  validateBrandMomentUploadContext,
  validateLogoUploadContext,
} from '../../utils';
import {
  useChannelSettingsLoadFailures,
  useIsMountedRef,
  useLoadingState,
  useProStudioRoutes,
  useProStudioViewerRole,
} from '../../hooks';
import { PROJECT_DATA_LOADED_IDLE_STATE } from '../../hooks/loading-states';
import {
  proStudioService,
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatchesOneOf,
} from '../../xstate/app.xstate';
import { nextChannelSettingsLoad } from '../../xstate/channel-settings-load-status';
import { waitForProStudioIdle } from '../../xstate/wait-for-pro-studio-idle';
import {
  UnsavedChangesValidationError,
  useEditChannelNameModal,
  useEditorModal,
  useUnsavedChanges,
} from '../modals';
import { ChannelSettingsTemplateSections } from './channel-settings-template-sections';
import {
  ChannelSettingsTabs,
  type ChannelSettingsTab,
  type ChannelSettingsTabLocationState,
} from './channel-settings-tabs';
import { ChannelSettingsHeader } from './channel-settings-header';
import { channelSaveSnackbarState } from './channel-save-snackbar';
import { OpenRealPreview } from './open-real-preview';
import { PypePreview } from './pype-preview';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

/**
 * Backstop for the `updateAssets` round trip (PSTUDIO-3897). Resolving `null`
 * routes into the existing "could not resolve the uploaded Brand Moment URL"
 * branch, so a dropped or lost event surfaces a real error instead of leaving
 * Save spinning forever.
 */
const ASSET_RESOLVE_TIMEOUT_MS = 30_000;

export type ChannelsDetailsFormValues = {
  name: string;
  env: string;
  pypeId: string;
  streamId: string;
  id?: string;
};

export type BrandingOptionsFormValues = {
  launcherName: string;
  widgetStyleColor: string;
  widgetStyleTextColor: string;
  agentBubbleColor: string;
  agentBubbleTextColor: string;
  userBubbleColor: string;
  userBubbleTextColor: string;
  buttonColor: string;
  textColor: string;
  name: string;
  bannerUrl: string;
  customPypeCss: string;
  brandMomentUrl: string;
  logo: string;
  logoFile?: File | null;
  brandMomentFile?: File | null;
};

export type SizingOptionsFormValues = {
  widgetWidth: number;
  widgetHeight: number;
  shouldLockRatio: boolean;
};

export interface DefaultFormValues {
  detailsFormValues?: ChannelsDetailsFormValues;
  brandingFormValues?: BrandingOptionsFormValues;
  sizingFormValues?: SizingOptionsFormValues;
}

interface ChannelSettingsFormProps {
  type: 'create' | 'update';
  values: DefaultFormValues;
}

// @TODO: JSON validation function from BOT-MANAGER-FRONTEND
// const validateJson = (stringifiedJson: string) => {
//   if (stringifiedJson) {
//     try {
//       JSON.parse(stringifiedJson);
//     } catch (e) {
//       return false;
//     }
//     return true;
//   }
//   return true;
// };

// @TODO: use it for color validation
// https://github.com/scttcper/tinycolor

const getWidthToHeightRatio = (width: number, height: number) => {
  let widthToHeightRatio = (width || 0) / (height || 0);
  if (isNaN(widthToHeightRatio) || widthToHeightRatio === Infinity) {
    widthToHeightRatio = 1;
  }
  return widthToHeightRatio;
};

export const ChannelSettingsForm: FC<ChannelSettingsFormProps> = ({
  type,
  values,
}) => {
  const [t] = useTranslation();
  const [widthToHeightRatio, setWidthToHeightRatio] = useState(1);
  const {
    projectId,
    botId,
    uniqueCustomerId,
    envs,
    pypeRuleGroups,
    channelApiCredentials,
    pypeConnections,
    ruleGroupsFailureCount,
    apiCredentialsFailureCount,
    liveAgentFailureCount,
  } = useProStudioCtxSelector((ctx) => ({
    envs: ctx.envs,
    projectId: ctx.project?.id,
    uniqueCustomerId: ctx.project?.projectProStudioSetting?.customerId,
    botId:
      ctx.customerId && ctx.botName
        ? `${ctx.customerId}.${ctx.botName}`
        : undefined,
    pypeRuleGroups: ctx.pypeRuleGroups,
    channelApiCredentials: ctx.channelApiCredentials,
    pypeConnections: ctx.pypeConnections,
    // Narrowed to the three counts on purpose: this selector's result is
    // compared shallowly, so returning the record OBJECT would re-render on
    // every machine transition once it exists.
    ruleGroupsFailureCount: ctx.channelSettingsLoadFailures?.ruleGroups,
    apiCredentialsFailureCount: ctx.channelSettingsLoadFailures?.apiCredentials,
    liveAgentFailureCount: ctx.channelSettingsLoadFailures?.liveAgentPlatforms,
  }));

  const routes = useProStudioRoutes();
  const isViewer = useProStudioViewerRole();
  const navigate = useNavigate();
  const location = useLocation();
  const isUpdating = type === 'update';

  const channelId = values.detailsFormValues?.id || '';
  // `env` is a free-form string on the form, but only these two channels exist
  // and the radio group is disabled on update — so the stored value is one of
  // them. Anything unexpected falls back to sandbox rather than building a live
  // URL for a channel that may not be live.
  const livePreviewEnv =
    values.detailsFormValues?.env?.toLowerCase() === 'live'
      ? 'live'
      : 'sandbox';

  // Returning from the Pype Next Theme tab (a separate route) carries the tab to
  // reopen in router state; a fresh visit starts on Settings.
  const initialTab: ChannelSettingsTab =
    (location.state as ChannelSettingsTabLocationState | null)?.channelTab ??
    'settings';

  // Object URL for the picked-but-not-yet-uploaded logo preview. Held in a ref
  // so it survives re-renders and is revoked on new-pick/remove/cancel/unmount
  // and after a successful Save-time PUT (PSTUDIO-3078).
  const logoPreviewUrlRef = React.useRef<string | null>(null);
  const revokeLogoPreview = React.useCallback(() => {
    if (logoPreviewUrlRef.current) {
      URL.revokeObjectURL(logoPreviewUrlRef.current);
      logoPreviewUrlRef.current = null;
    }
  }, []);
  // revoke on unmount
  React.useEffect(() => revokeLogoPreview, [revokeLogoPreview]);

  // Stable deferred-upload handler — the factory is pure, so memoize it once to
  // avoid handing the Filepond WC a fresh `onUpload` on every re-render (the
  // form re-renders on every watched field; mirrors use-knowledge-agent-upload).
  const deferredLogoUpload = React.useMemo(
    () => createDeferredFileUpload(),
    []
  );

  // Object URL for the picked-but-not-yet-uploaded Brand Moment preview. Same
  // lifecycle as the logo preview (PSTUDIO-3710 mirrors PSTUDIO-3078).
  const brandMomentPreviewUrlRef = React.useRef<string | null>(null);
  const revokeBrandMomentPreview = React.useCallback(() => {
    if (brandMomentPreviewUrlRef.current) {
      URL.revokeObjectURL(brandMomentPreviewUrlRef.current);
      brandMomentPreviewUrlRef.current = null;
    }
  }, []);
  React.useEffect(() => revokeBrandMomentPreview, [revokeBrandMomentPreview]);

  const deferredBrandMomentUpload = React.useMemo(
    () => createDeferredFileUpload(),
    []
  );

  const detailsForm = useForm<ChannelsDetailsFormValues>({
    defaultValues: values.detailsFormValues || {},
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const brandingForm = useForm<BrandingOptionsFormValues>({
    defaultValues: values.brandingFormValues,
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const sizingForm = useForm<SizingOptionsFormValues>({
    defaultValues: values.sizingFormValues,
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  // Renaming writes back into this form rather than mutating on its own: the
  // widget PUT always carries `style` / `beta` / `custom_pype_css`, so a
  // name-only call from the modal would blank the channel's branding. The
  // shared Save Changes below persists it with everything else.
  // Channel Name has no rendered input on the update path any more (the card is
  // create-only), so `register` is never called from the JSX and
  // `detailsForm.trigger()` would validate nothing. Register it here so a blank
  // name still blocks Save instead of PUTting `widget_name: ''` — the modal only
  // guards names the user actually types, not one that arrived blank.
  useEffect(() => {
    if (!isUpdating) {
      return;
    }
    detailsForm.register('name', {
      required:
        t('pro-studio/channel-details:form.validation-messages.name') ||
        'Channel name is required',
      validate: (value) =>
        value.trim().length > 0 || 'Channel name is required',
    });
  }, [isUpdating, detailsForm, t]);

  const { editChannelNameModal, openModal: openRenameModal } =
    useEditChannelNameModal({
      currentName: detailsForm.watch('name'),
      onSave: (name) =>
        detailsForm.setValue('name', name, { shouldDirty: true }),
    });

  const { editorModal: cssEditorModal, openModal: openCssEditorModal } =
    useEditorModal({
      title: 'Custom CSS',
      langs: [langs.css()],
      code: brandingForm.getValues('customPypeCss'),
      onSave: (fileData) => {
        brandingForm.setValue('customPypeCss', fileData, { shouldDirty: true });
      },
    });

  // Auto-masking rule set (channel-level). The selection persists as part of
  // the channel's "Save Changes"; a change marks the form dirty like any other.
  const channelPypeId = detailsForm.watch('pypeId');
  const channelEnv = detailsForm.watch('env');
  // The rule set currently assigned to this channel's pype+env ('' = none
  // assigned, i.e. the channel falls back to the project default).
  const initialRuleSetId = useMemo(() => {
    if (!channelPypeId || !channelEnv) {
      return '';
    }
    const assigned = (pypeRuleGroups ?? []).find((group) =>
      group.pypes.some(
        (pype) =>
          pype.pypeId === channelPypeId &&
          pype.env.toLowerCase() === channelEnv.toLowerCase()
      )
    );
    return assigned?.id ?? '';
  }, [pypeRuleGroups, channelPypeId, channelEnv]);
  // `null` = untouched (mirror the persisted value); a string = user picked one.
  const [selectedRuleSetId, setSelectedRuleSetId] = useState<string | null>(
    null
  );
  // The Select's value, the dirty-flag and the mutation payload all come from
  // ONE resolver (PSTUDIO-4051): the dropdown normalises an explicit binding to
  // the project default onto the empty-valued option, so the dirty-check has to
  // compare NORMALISED with normalised while the payload keeps the RAW ids.
  // Deriving both in the same place is what stops them drifting apart — and is
  // what makes the round trip unit-testable.
  const maskingAssignment = resolveMaskingAssignment(
    pypeRuleGroups ?? [],
    initialRuleSetId,
    selectedRuleSetId
  );
  const effectiveRuleSetId = maskingAssignment.selectValue;
  // Until the rule sets are in, `initialRuleSetId` reads '' for EVERY channel —
  // a bound one included. A pick made before an invalidation (the refetch a
  // channel create/delete triggers) would then look dirty against a binding we
  // cannot see yet, and Save would send `from: undefined`, orphaning the old
  // membership. No data, no dirty masking.
  const maskingTouched = pypeRuleGroups != null && maskingAssignment.touched;

  // The template blocks (Live Agent Platforms / Reporting API / Auto-masking)
  // render only when EDITING an existing channel — on create the pype doesn't
  // exist yet, so there's nothing to read from or attach to. Load their data
  // once, and SERIALIZE the three loads: each event is handled only in the
  // machine's `idle` state, so firing all three together drops the 2nd/3rd (the
  // first transitions the machine out of idle). Dispatching one at a time —
  // advancing as each completes — avoids the drop and the empty-after-refresh
  // race.
  // Guards the Save button while the deferred logo / Brand Moment PUT is in
  // flight. `touched` only clears after the channel mutation's cb() runs, so
  // without this the button stays enabled during the upload and a double-click
  // re-enters onSubmit and fires a second PUT (PSTUDIO-3078 review).
  //
  // Declared ABOVE the load chain because the chain now reads it: the two
  // compete for the same `idle` window (PSTUDIO-4253).
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPageLoading = useLoadingState();
  const ruleGroupsLoaded = pypeRuleGroups != null;
  // API keys are scoped to a pype, so a channel without one has none to fetch
  // and none to show. Counting that as "loaded" keeps the chain moving to the
  // live-agent step instead of dispatching a load that could only fail — the
  // project-wide loader this replaced never needed the channel's own pype
  // (PSTUDIO-4253).
  const channelHasPype = Boolean(channelPypeId && channelEnv);
  // Loaded means loaded FOR THIS CHANNEL: the slice carries the pype and env it
  // was fetched for, so switching channels re-asks instead of rendering the
  // previous channel's keys (PSTUDIO-4253).
  const apiCredentialsLoaded =
    !channelHasPype ||
    (channelApiCredentials != null &&
      channelApiCredentials.pypeId === channelPypeId &&
      channelApiCredentials.env === channelEnv);
  // Gate on `pypeConnections`, NOT on `liveAgentPypes`. Seven flows write
  // `liveAgentPypes` — `loadApiCredentials`, which runs one step earlier in this
  // very chain, is one of them — so keying off it made this step look already
  // done and `loadLiveAgentPlatforms` never fired. The dropdown then had no
  // connections to list and no way to resolve the channel's current binding, so a
  // reload showed "No live agent platforms configured" on a bound channel.
  // `pypeConnections` is set only by `loadLiveAgentPlatforms`, so it actually
  // means "the loader ran".
  const liveAgentLoaded = pypeConnections != null;
  // Which of the three loads THIS form has dispatched, and which of them its own
  // dispatch saw fail. `channelSettingsLoadFailures` is PROJECT-scoped and the
  // same three loads are dispatched by the Data Privacy / Reporting API
  // Credentials / Live Agent Platforms screens too, which gate only on their own
  // slice being `null` and never read or clear the record — so a failure raised
  // on one of those, or still in flight when this page opened, outlives the page
  // that saw it. Read as a bare flag it made this page treat the step as
  // already-settled-and-failed: no dispatch at all, and the permanent
  // "Couldn't load … Reload the page" note on a block that had never tried.
  //
  // The decision lives in `useChannelSettingsLoadFailures` so a unit test can
  // reach it — this form pulls in design-system Lit components and cannot be
  // rendered in this package's test environment.
  const {
    ruleGroupsFailed,
    apiCredentialsFailed,
    liveAgentFailed,
    recordDispatch,
  } = useChannelSettingsLoadFailures({
    ruleGroupsLoaded,
    apiCredentialsLoaded,
    liveAgentLoaded,
    ruleGroupsFailureCount,
    apiCredentialsFailureCount,
    liveAgentFailureCount,
  });
  // PSTUDIO-3897: these three loads are ALSO `idle`-only, and `isPageLoading` is
  // deliberately false while they run (their flows are in
  // `NON_LOADING_STUDIO_STATES`), so it can't stand in for "the machine is free".
  // Gating on the real idle state stops a step being dispatched into a busy
  // machine and dropped — which used to stall the whole chain until the page was
  // remounted, since the effect only re-runs when one of the flags flips.
  const isMachineIdle = useProStudioStateMatchesOneOf([
    PROJECT_DATA_LOADED_IDLE_STATE,
  ]);
  useEffect(() => {
    if (!isUpdating || isPageLoading || !isMachineIdle) {
      return;
    }
    // A step that FAILED counts as settled, not as pending — the decision lives
    // in `nextChannelSettingsLoad` so it can be unit tested (no RTL here); this
    // effect only carries it out.
    const next = nextChannelSettingsLoad({
      ruleGroupsLoaded,
      ruleGroupsFailed,
      apiCredentialsLoaded,
      apiCredentialsFailed,
      liveAgentLoaded,
      liveAgentFailed,
      isSaving: isSubmitting,
    });

    if (next === 'ruleGroups') {
      // Record the snapshot BEFORE dispatching: a synchronous failure would
      // otherwise be counted against a snapshot that does not exist yet, and
      // the step would never settle.
      recordDispatch('ruleGroups', ruleGroupsFailureCount);
      sendProStudioEvent({ type: 'loadPypeRuleGroups' });
    } else if (next === 'apiCredentials') {
      recordDispatch('apiCredentials', apiCredentialsFailureCount);
      // Scoped to this channel's own pype + env. The project-wide
      // `loadApiCredentials` belongs to the Reporting API Credentials screen and
      // costs one request per pype per env — which every save on this page then
      // waited behind (PSTUDIO-4253).
      sendProStudioEvent({
        type: 'loadChannelApiCredentials',
        payload: {
          pypeId: channelPypeId,
          env: channelEnv as 'live' | 'sandbox',
        },
      });
    } else if (next === 'liveAgentPlatforms') {
      recordDispatch('liveAgentPlatforms', liveAgentFailureCount);
      sendProStudioEvent({ type: 'loadLiveAgentPlatforms' });
    }
  }, [
    isUpdating,
    isPageLoading,
    isMachineIdle,
    isSubmitting,
    channelPypeId,
    channelEnv,
    ruleGroupsLoaded,
    apiCredentialsLoaded,
    liveAgentLoaded,
    ruleGroupsFailed,
    apiCredentialsFailed,
    liveAgentFailed,
    ruleGroupsFailureCount,
    apiCredentialsFailureCount,
    liveAgentFailureCount,
    recordDispatch,
  ]);

  // Credentials whose key lives on this channel's pype + env. The slice is
  // already scoped to exactly that, but it is still checked against the current
  // pype/env: a stale slice from the channel visited before must render as "not
  // loaded", never as this channel's keys (PSTUDIO-4253).
  const channelCredentials = useMemo(
    () =>
      channelApiCredentials?.pypeId === channelPypeId &&
      channelApiCredentials?.env === channelEnv
        ? channelApiCredentials.rows
        : [],
    [channelApiCredentials, channelEnv, channelPypeId]
  );

  // Dropdown options: the project's connections for this channel's env. The logo
  // comes off each connection's `platformId`, which already names the platform.
  const platformConnections = useMemo(() => {
    const env = channelEnv as 'live' | 'sandbox';
    return (pypeConnections ?? [])
      .filter((connection) => toChannelEnv(connection.environment) === env)
      .map((connection) => ({
        id: connection.id,
        name: connection.name,
        agentRouting: connection.agentRouting,
        logo: getPlatformLogo(connection.platformId),
      }));
  }, [pypeConnections, channelEnv]);

  // The connection this channel is bound to — a direct lookup in the join rows,
  // no value matching. Mirrors how the masking block resolves its rule set.
  const initialConnectionId = useMemo(() => {
    if (!channelPypeId || !channelEnv) {
      return '';
    }
    const bound = (pypeConnections ?? []).filter((connection) =>
      connection.pypes.some(
        (pype) =>
          pype.pypeId === channelPypeId &&
          pype.env.toLowerCase() === channelEnv.toLowerCase()
      )
    );
    // The dropdown is single-select, but the schema permits a pype in several
    // connections at once (the unique index is per connection+pype+env, not per
    // pype+env). The UI can't express that, and switching would only detach the
    // one shown — so surface it instead of silently picking one.
    if (bound.length > 1) {
      console.warn(
        '[channel-settings] pype is bound to multiple live agent platform connections; showing the first',
        {
          pypeId: channelPypeId,
          env: channelEnv,
          connectionIds: bound.map((connection) => connection.id),
        }
      );
    }
    return bound[0]?.id ?? '';
  }, [pypeConnections, channelPypeId, channelEnv]);

  // `null` = untouched (mirror the persisted value); a string = user picked one.
  const [pickedConnectionId, setPickedConnectionId] = useState<string | null>(
    null
  );
  const selectedConnectionId = pickedConnectionId ?? initialConnectionId;
  const lapTouched =
    pickedConnectionId !== null && pickedConnectionId !== initialConnectionId;

  // Recording the pick is all the form does. The routing values are owned by the
  // connection and written onto this channel's pype by the backend when the
  // membership changes, so nothing is copied onto the channel form here.
  const onSelectConnection = (connectionId: string) => {
    setPickedConnectionId(connectionId);
  };

  const formTouched = detailsForm.formState.isDirty;
  const brandingsTouched = brandingForm.formState.isDirty;
  const sizingTouched = sizingForm.formState.isDirty;
  // `lapTouched` and `maskingTouched` are listed explicitly: both persist through
  // their own mutations, not through a form field, so neither shows up as dirty.
  const touched =
    formTouched ||
    brandingsTouched ||
    sizingTouched ||
    maskingTouched ||
    lapTouched;

  const [snackbarDismissed, setSnackbarDismissed] = useState(false);

  // PSTUDIO-4253: the Save button and its spinner live INSIDE this snackbar, and
  // `submitChannel` dismisses the snackbar before it dispatches anything — so
  // the whole `awaitMachineIdle` wait used to happen with nothing on screen.
  // That wait is the reporter's "Save changes gives no visible feedback".
  const saveSnackbar = channelSaveSnackbarState({
    touched,
    dismissed: snackbarDismissed,
    saving: isSubmitting,
  });

  useEffect(() => {
    if (touched) {
      setSnackbarDismissed(false);
    }
  }, [touched]);

  const handleFormReset = () => {
    detailsForm.reset();
    brandingForm.reset();
    sizingForm.reset();
    setSelectedRuleSetId(null);
  };

  // PSTUDIO-3897. EVERY machine event this form dispatches — `updateAssets`,
  // `assignPypeRuleGroup`, `assignPypeConnection`, `updateChannel` — is handled
  // ONLY in `idle`, and Channel Settings keeps the machine busy for seconds
  // after mount. Gate each dispatch on this rather than waiting once up front:
  // the uploads in between take real time, during which a background flow can
  // make the machine busy again.
  const awaitMachineIdle = async () => {
    try {
      await waitForProStudioIdle(proStudioService);
    } catch (e) {
      sendProStudioEvent({
        type: 'showError',
        error:
          t('pro-studio/channel-details:form.errors.save-timeout') ||
          'Your changes could not be saved. Please try again.',
      });
      setSnackbarDismissed(false);
      throw e instanceof Error ? e : new Error(String(e));
    }
  };

  // The form can be discarded mid-save: the unsaved-changes guard's "Leave"
  // proceeds the blocker and unmounts immediately, without cancelling a save
  // parked in `awaitMachineIdle`. `proStudioService` is a module singleton, so
  // the queued dispatch would still fire and persist edits the user just chose
  // to discard.
  //
  // The ref must be re-armed on every effect SETUP, which is why it lives in a
  // hook: this used to be a cleanup-only effect, and React 18's StrictMode
  // replay (setup → cleanup → setup) then left it `false` for the component's
  // whole life in the DEV build — so every Save on this page aborted as
  // `save-abandoned` before dispatching, with no mutation, no toast and no
  // navigation. CI could never see it: it runs the suite against a deployed
  // production build, where React does not double-invoke (see the hook).
  const isMountedRef = useIsMountedRef();

  // Callbacks are pluggable so one submit path serves both the form's own Save
  // (navigate away) and the unsaved-changes guard (resolve, never navigate).
  //
  // Every abort below THROWS rather than returning: the guard awaits this
  // promise, so a silent early return would leave its modal stuck on "Saving…"
  // with no way to reach the error the abort just surfaced.
  const submitChannel = (callbacks: {
    cb: () => void;
    errorCb?: () => void;
  }): Promise<void> => {
    // Re-entrancy guard: the Save button's loading/disabled already blocks a
    // second click, but bail defensively in case this is reached another
    // way (keyboard, programmatic, the guard modal) while an upload is in flight.
    if (isSubmitting) {
      return Promise.reject(new Error('save-already-in-flight'));
    }
    setSnackbarDismissed(true);
    setIsSubmitting(true);
    const detailsValid = detailsForm.trigger();
    const brandingValid = brandingForm.trigger();
    const sizingValid = sizingForm.trigger();

    return Promise.all([detailsValid, brandingValid, sizingValid])
      .then(async ([isDetailsValid, isBrandingValid, isSizingValid]) => {
        if (!isDetailsValid || !isBrandingValid || !isSizingValid) {
          setSnackbarDismissed(false);
          // Signals the guard to close and hand the form back: the invalid
          // fields sit behind the modal, so "try again" can never succeed.
          throw new UnsavedChangesValidationError();
        }

        const channelDetailsData = detailsForm.getValues();
        const brandingData = brandingForm.getValues();
        const sizingData = sizingForm.getValues();

        // Deferred Brand Moment upload (PSTUDIO-3710): if a File was picked, PUT
        // it now, resolve its asset URL via `updateAssets`, and use that URL in
        // the mutation payload. On failure, surface the error + abort navigation.
        let resolvedBrandMomentUrl = brandingData.brandMomentUrl;
        let brandMomentUnresolved = false;
        const { brandMomentFile } = brandingData;
        if (brandMomentFile instanceof File) {
          const missingField = validateBrandMomentUploadContext({
            projectId,
            botId,
          });
          if (missingField) {
            const fallbackByField: Record<
              BrandMomentUploadContextField,
              string
            > = {
              projectId: 'Project ID is required',
              botId: 'Bot ID is required',
            };
            sendProStudioEvent({
              type: 'showError',
              error:
                t(
                  `pro-studio/channel-details:form.validation-messages.${missingField}`
                ) || fallbackByField[missingField],
            });
            setSnackbarDismissed(false);
            throw new Error(`brand-moment-context-${missingField}`);
          }
          const brandMomentUploadUrl = buildBrandMomentUploadUrl({
            adminUrl: import.meta.env.FE_ADMIN_URL,
            projectId: projectId as string,
            entityId: botId as string,
          });
          try {
            const uploadResult = await uploadBrandMomentFile({
              url: brandMomentUploadUrl,
              file: brandMomentFile,
            });
            // Resolve the final asset URL through the existing updateAssets
            // invoke (GraphQL getAssetById), promisified so we can await it
            // before sending the channel mutation.
            //
            // `updateAssets` is idle-only AND — unlike the channel mutations —
            // has no bubble-up handler in `projectDataLoaded`, so dispatching it
            // while the machine is busy drops it outright: `cb` never fires, the
            // promise below never settles, and Save spins forever with no error
            // and no navigation. Wait for idle first, and still bound the wait so
            // a dropped event can never wedge the button permanently.
            await awaitMachineIdle();
            const resolvedUrl = await new Promise<string | null>((resolve) => {
              const settleEmpty = setTimeout(
                () => resolve(null),
                ASSET_RESOLVE_TIMEOUT_MS
              );
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore - updateAssets event cb signature not reflected in ProStudioEvents union
              sendProStudioEvent({
                type: 'updateAssets',
                data: uploadResult.data,
                cb: (assets) => {
                  clearTimeout(settleEmpty);
                  resolve(
                    resolveBrandMomentUrl(assets, uploadResult.pictureId)
                  );
                },
              });
            });
            if (resolvedUrl) {
              resolvedBrandMomentUrl = resolvedUrl;
              // Keep the in-form field honest until navigation (not dirtying).
              brandingForm.setValue('brandMomentUrl', resolvedUrl, {
                shouldDirty: false,
              });
              revokeBrandMomentPreview();
            } else {
              // Upload succeeded but the asset URL could not be resolved — do NOT persist
              // the transient blob: object URL. Surface an error and stay on the form.
              //
              // This branch is exercised when `updateAssets` returns an empty
              // asset list (resolveBrandMomentUrl([], pictureId) → null) — e.g.
              // the bot has no assets array, or the invoke errored and settled
              // cb([]) (the resolve-timeout-equivalent). Dedicated e2e coverage
              // of this path is deferred: it needs a GraphQL empty-assets
              // fixture, tracked separately (PSTUDIO-3710 review, finding F3).
              sendProStudioEvent({
                type: 'showError',
                error:
                  t(
                    'pro-studio/channel-details:form.validation-messages.brand-moment-upload'
                  ) || 'Could not resolve the uploaded Brand Moment URL',
              });
              setSnackbarDismissed(false);
              // Flagged rather than thrown here: a throw inside this `try`
              // would be caught below and surfaced a second time.
              brandMomentUnresolved = true;
            }
          } catch (e) {
            sendProStudioEvent({ type: 'showError', error: String(e) });
            setSnackbarDismissed(false);
            // do NOT navigate away on Brand Moment failure
            throw e instanceof Error ? e : new Error(String(e));
          }
          if (brandMomentUnresolved) {
            throw new Error('brand-moment-url-unresolved');
          }
        }

        const commonProps = {
          name: channelDetailsData.name,
          pypeId: channelDetailsData.pypeId,
          streamId: channelDetailsData.streamId,
          env: channelDetailsData.env as 'live' | 'sandbox',
          style: {
            name: brandingData.name,
            shouldLockRatio: sizingData.shouldLockRatio,
            widgetHeight: +sizingData.widgetHeight,
            widgetWidth: +sizingData.widgetWidth,
            widgetStyleColor: brandingData.widgetStyleColor,
            widgetStyleTextColor: brandingData.widgetStyleTextColor,
            agentBubbleColor: brandingData.agentBubbleColor,
            agentBubbleTextColor: brandingData.agentBubbleTextColor,
            userBubbleColor: brandingData.userBubbleColor,
            userBubbleTextColor: brandingData.userBubbleTextColor,
            bannerUrl: brandingData.bannerUrl,
          },
          beta: {
            customStyling: {
              launcherName: brandingData.launcherName,
              brandMomentUrl: resolvedBrandMomentUrl,
              buttonColor: brandingData.buttonColor,
              textColor: brandingData.textColor,
            },
          },
          customPypeCss: brandingData.customPypeCss,
          ...callbacks,
        };

        const props = isUpdating
          ? {
              type: 'updateChannel' as const,
              payload: {
                id: channelDetailsData.id || '',
                ...commonProps,
              },
            }
          : {
              type: 'createChannel' as const,
              payload: {
                ...commonProps,
              },
            };

        const { logoFile } = brandingData;
        if (logoFile instanceof File) {
          const missingField = validateLogoUploadContext({
            projectId,
            env: channelDetailsData.env,
            uniqueCustomerId,
          });
          if (missingField) {
            const fallbackByField: Record<LogoUploadContextField, string> = {
              projectId: 'Project ID is required',
              env: 'Environment is required',
              customerId: 'Customer ID is required',
            };
            sendProStudioEvent({
              type: 'showError',
              error:
                t(
                  `pro-studio/channel-details:form.validation-messages.${missingField}`
                ) || fallbackByField[missingField],
            });
            setSnackbarDismissed(false);
            throw new Error(`logo-context-${missingField}`);
          }
          const logoUrl = buildLogoUploadUrl({
            adminUrl: import.meta.env.FE_ADMIN_URL,
            projectId: projectId as string,
            env: channelDetailsData.env,
            uniqueCustomerId: uniqueCustomerId as string,
            pypeId: channelDetailsData.pypeId,
          });
          try {
            const resolvedLogoUrl = await uploadLogoFile({
              url: logoUrl,
              file: logoFile,
            });
            // Replace the transient object URL with the real server URL before
            // revoking, so a delayed navigation can't paint a revoked blob: src
            // (PSTUDIO-3078 review). `logo` is NOT part of the channel mutation
            // payload — this only keeps the in-form preview honest until nav.
            if (resolvedLogoUrl) {
              brandingForm.setValue('logo', resolvedLogoUrl, {
                shouldDirty: false,
              });
            }
            revokeLogoPreview();
          } catch (e) {
            sendProStudioEvent({ type: 'showError', error: String(e) });
            setSnackbarDismissed(false);
            // do NOT navigate away on logo failure
            throw e instanceof Error ? e : new Error(String(e));
          }
        }

        const channelDirty = formTouched || brandingsTouched || sizingTouched;

        // The template-block assignments (masking, live agent platform) only
        // happen on EDIT — the blocks are edit-only, the pype must already
        // exist. Each is its own machine flow, and the machine handles these
        // events only in `idle`, so they must be SERIALIZED: the channel update
        // invokes its callback mid-flow (inside mutateChannel, before the
        // machine is back in `idle`), while an assignment flow runs its callback
        // on completion. So dispatch the assignments first, chaining each off
        // the previous one's callback, and hang the channel save off the last.
        const assignments: Array<(next?: () => void) => void> = [];

        if (isUpdating && maskingTouched) {
          assignments.push((next) =>
            sendProStudioEvent({
              type: 'assignPypeRuleGroup',
              payload: {
                pypeId: channelDetailsData.pypeId,
                env: channelDetailsData.env as 'live' | 'sandbox',
                fromGroupId: maskingAssignment.fromGroupId,
                toGroupId: maskingAssignment.toGroupId,
                cb: next,
              },
            })
          );
        }

        if (isUpdating && lapTouched) {
          assignments.push((next) =>
            sendProStudioEvent({
              type: 'assignPypeConnection',
              payload: {
                pypeId: channelDetailsData.pypeId,
                env: channelDetailsData.env as 'live' | 'sandbox',
                fromConnectionId: initialConnectionId || undefined,
                toConnectionId: selectedConnectionId || undefined,
                cb: next,
              },
            })
          );
        }

        // With no assignments the channel save always runs (that was the old
        // else-branch); with assignments it runs only if something else changed.
        const saveChannel =
          assignments.length === 0 || channelDirty
            ? () => sendProStudioEvent(props)
            : callbacks.cb;

        // PSTUDIO-3897: every event dispatched below — the two assignments and
        // the channel mutation itself — is handled ONLY in the machine's `idle`
        // state. Sent while a flow is running they bubble to `projectDataLoaded`
        // and are discarded, which is how a save could produce no mutation, no
        // error and no navigation. The on-mount load chain above keeps the
        // machine busy for seconds while the page looks completely ready, so
        // this is the common case, not a corner. Wait for the machine to be free
        // instead of firing into the void.
        await awaitMachineIdle();

        // The wait can span seconds, and the unsaved-changes guard's "Leave"
        // unmounts this form the moment the user takes it. Dispatching now would
        // persist edits they explicitly chose to discard — and call `navigate`
        // from a dead closure — so drop the save instead.
        if (!isMountedRef.current) {
          throw new Error('save-abandoned');
        }

        assignments.reduceRight<(() => void) | undefined>(
          (next, dispatch) => () => dispatch(next),
          saveChannel
        )?.();
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSaveAndLeave = () =>
    new Promise<void>((resolve, reject) => {
      submitChannel({
        cb: resolve,
        errorCb: () => reject(new Error('save-failed')),
      }).catch(reject);
    });

  const { modal: unsavedChangesModal, allowNavigationOnce } = useUnsavedChanges(
    { isDirty: touched, onSave: handleSaveAndLeave }
  );

  const onCancel = () => {
    setSnackbarDismissed(true);
    revokeLogoPreview();
    revokeBrandMomentPreview();
    handleFormReset();
    allowNavigationOnce();
    navigate(routes.channels.path);
  };

  const onSubmit = () => {
    // PSTUDIO-3897: this path used to pass `cb` only. When the machine discarded
    // the mutation its bubble-up handler called `errorCb?.()` — undefined — so
    // the click did literally nothing: no mutation, no toast, no navigation, the
    // snackbar still open on a still-dirty form. Whatever happens now, exactly
    // one of these fires (the settlement also times out on a path that settles
    // neither), so a failed save is always visible and always retryable.
    const settlement = createSaveSettlement({
      onSuccess: () => {
        allowNavigationOnce();
        navigate(routes.channels.path);
        handleFormReset();
      },
      onFailure: () => {
        sendProStudioEvent({
          type: 'showError',
          error:
            t('pro-studio/channel-details:form.errors.save-failed') ||
            'Your changes could not be saved. Please try again.',
        });
        // Bring the unsaved-changes snackbar back so the edits — which are still
        // on screen and still dirty — can be saved again.
        setSnackbarDismissed(false);
      },
    });

    void submitChannel({
      cb: settlement.cb,
      errorCb: settlement.errorCb,
    }).catch(() => {
      // Abort paths (invalid fields, logo / brand-moment upload failure, the
      // idle-wait timeout) each surface their own error before rejecting, and
      // none of them dispatched anything — so drop the pending settlement rather
      // than letting its timeout fire a second, misleading toast.
      settlement.cancel();
    });
  };

  // The "Pype Theme" tab: everything that describes how the widget LOOKS, plus
  // the mock preview that echoes it. On the create page this is the whole form —
  // there are no tabs until a channel exists (PSTUDIO-3768).
  const themeTabContent = (
    <>
      <Grid
        test-id="ps-channel-settings-form-grid"
        id="ps-channel-settings-form-grid"
        gutter="large"
      >
        <GridCell xsmall="12" large="6">
          {isUpdating && channelId && (
            <OpenRealPreview channelId={channelId} env={livePreviewEnv} />
          )}
          {/* 10px gap to the template sections below (DS default is 24px). */}
          <PageSection size="small" style={{ marginBottom: '10px' }}>
            {/* Channel Details is CREATE-only. On an existing channel the name
                lives in the page header (renamed from its kebab menu) and the
                environment is the badge beside it — it was already read-only
                after creation anyway (PSTUDIO-3768). */}
            {!isUpdating && (
              <>
                <Stack
                  justifyContent="space-between"
                  alignItems="center"
                  gutter="small"
                >
                  <TextSubtitle test-id="ps-channel-details-title">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.channel-details">
                      Channel Details
                    </TranslationComponent>
                  </TextSubtitle>
                  <Stack
                    test-id="ps-channel-details-pype-logo-stack"
                    alignItems="center"
                    gutter="xsmall"
                    nowrap
                  >
                    <Image
                      width="24"
                      height="24"
                      src={PypeLogo as unknown as string}
                    />
                    <TextBody>Pype</TextBody>
                  </Stack>
                </Stack>
                <Spacer />
                <TextBody test-id="ps-channel-name-label" size="small">
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.channel-name">
                    Channel Name
                  </TranslationComponent>
                </TextBody>
                <Spacer size="2xsmall" />
                <Input
                  test-id={'ps-create-channel-name-input'}
                  placeholder={
                    t(
                      'pro-studio/channel-details:form.placeholders.channel-name'
                    ) || 'Channel Name'
                  }
                  value={detailsForm.watch('name')}
                  helpText={detailsForm.formState.errors.name?.message}
                  hasError={!!detailsForm.formState.errors.name}
                  variant="outlined"
                  {...detailsForm.register('name', {
                    required:
                      t(
                        'pro-studio/channel-details:form.validation-messages.name'
                      ) || 'Channel name is required',
                  })}
                  // @ts-ignore
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    detailsForm.trigger('name');
                    detailsForm.setValue('name', e.target.value, {
                      shouldDirty: true,
                    });
                  }}
                  disabled={isViewer}
                />
                <Spacer />
                <TextBody test-id="ps-channel-environment-label" size="small">
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.environment">
                    Environment
                  </TranslationComponent>
                </TextBody>
                <Spacer size="2xsmall" />
                <RadioGroup
                  value={detailsForm.watch('env')}
                  hasError={!!detailsForm.formState.errors.env}
                  orientation="horizontal"
                  test-id="ps-create-channel-environment-radio-group"
                  helpText={
                    detailsForm.formState.errors.env?.message
                      ? detailsForm.formState.errors.env?.message
                      : t('pro-studio/channel-details:form.help-texts.env') ||
                        'Cannot be changed after channel is created.'
                  }
                  {...detailsForm.register('env', {
                    required:
                      t(
                        'pro-studio/channel-details:form.validation-messages.env'
                      ) || 'Environment is required',
                  })}
                  onChange={(e) => {
                    const env = e.target.value;
                    detailsForm.setValue('env', env, { shouldDirty: true });
                    detailsForm.trigger('env');
                  }}
                >
                  {[...(envs || [])]?.reverse().map((env) => (
                    <Radio
                      checked={
                        detailsForm.watch('env').toLowerCase() ===
                        env.toLowerCase()
                      }
                      disabled={isViewer}
                      key={env}
                      value={env}
                    >
                      {capitalizeWords(env)}
                    </Radio>
                  ))}
                </RadioGroup>

                <Spacer size="xlarge" />
                <Divider />
                <Spacer size="xlarge" />
              </>
            )}

            <form>
              <Stack alignItems="center" justifyContent="space-between">
                <Stack alignItems="center" gutter="small">
                  <TextSubtitle test-id="ps-branding-options-title">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.branding-options">
                      Branding options
                    </TranslationComponent>
                  </TextSubtitle>
                  <Button
                    test-id="ps-channel-branding-options-reset-button"
                    size="small"
                    disabled={!brandingsTouched || isViewer}
                    variant="ghost"
                    onClick={() => brandingForm.reset()}
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.buttons.reset">
                      Reset
                    </TranslationComponent>
                  </Button>
                </Stack>
                {!isViewer && (
                  <>
                    <Button
                      test-id="ps-edit-style-sheet-button"
                      size="small"
                      variant="secondary"
                      onClick={openCssEditorModal}
                    >
                      <Icon name="pencil" size="xsmall" slot="prefix" />
                      <TranslationComponent i18nKey="pro-studio/channel-details:form.buttons.edit">
                        Edit Style Sheet
                      </TranslationComponent>
                    </Button>
                    {cssEditorModal}
                  </>
                )}
              </Stack>
              <Spacer />

              <TextBody test-id="ps-channel-launcher-name-label" size="small">
                <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.launcher-name">
                  Launcher Name
                </TranslationComponent>
              </TextBody>
              <Spacer size="2xsmall" />
              <Input
                test-id="ps-channel-launcher-name-input"
                variant="outlined"
                placeholder={
                  t(
                    'pro-studio/channel-details:form.placeholders.launcher-name'
                  ) || 'Engage with us'
                }
                value={brandingForm.watch('launcherName')}
                {...brandingForm.register('launcherName')}
                // @ts-expect-error - design-system Input event type differs from React.ChangeEvent
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  brandingForm.setValue('launcherName', e.target.value, {
                    shouldDirty: true,
                  });
                }}
                disabled={isViewer}
              />
              <Spacer size="large" />

              {type === 'update' && (
                <>
                  <TextBody test-id="ps-channel-logo-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.logo">
                      Logo
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <Input
                    test-id="ps-update-channel-logo-input"
                    variant="outlined"
                    placeholder={
                      t('pro-studio/channel-details:form.placeholders.logo') ||
                      'Logo URL'
                    }
                    value={brandingForm.watch('logo')}
                    {...brandingForm.register('logo')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('logo', value, {
                        shouldDirty: true,
                      });
                    }}
                    // Read-only for now: a typed logo URL is NOT persisted — `logo`
                    // is not part of the channel mutation payload, so a logo can
                    // only be saved via the file PUT below (PSTUDIO-3078 documented
                    // this as out of scope, pending backend AUTO-4598 / AUTO-4599).
                    // Leaving it typeable would let users enter a URL, see the
                    // preview update, Save — and silently lose it on reload. The
                    // field still shows the current server logo. Re-enable by
                    // restoring `disabled={isViewer}` once the backend persists it.
                    disabled
                  />
                  {!isViewer && (
                    <>
                      <TextBody
                        test-id="ps-channel-logo-or-label"
                        size="small"
                        textAlign="center"
                      >
                        <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.or">
                          or
                        </TranslationComponent>
                      </TextBody>
                      <Filepond
                        test-id="ps-update-channel-logo-filepond"
                        entityType={EntityTypes.ASSET}
                        entityId={detailsForm.getValues('pypeId')}
                        onUpload={deferredLogoUpload}
                        pictureSrc={brandingForm.watch('logo')}
                        readonly={isViewer}
                        // Removing a logo is not persisted yet (no backend
                        // delete — AUTO-4598 / AUTO-4599), so hide the remove
                        // affordance while keeping upload usable (PSTUDIO-3078).
                        hideRemove
                        onRemove={() => {
                          revokeLogoPreview();
                          brandingForm.setValue('logoFile', null, {
                            shouldDirty: true,
                          });
                          brandingForm.setValue('logo', '', {
                            shouldDirty: true,
                          });
                        }}
                        onChange={(p) => {
                          const file = p?.files?.[0];
                          if (!(file instanceof File)) {
                            return;
                          }
                          revokeLogoPreview();
                          const previewUrl = URL.createObjectURL(file);
                          logoPreviewUrlRef.current = previewUrl;
                          // Hold the raw File for the deferred Save-time PUT; the
                          // object URL drives both the Filepond avatar and the
                          // PypePreview <img> until Save (PSTUDIO-3078).
                          brandingForm.setValue('logoFile', file, {
                            shouldDirty: true,
                          });
                          brandingForm.setValue('logo', previewUrl, {
                            shouldDirty: true,
                          });
                        }}
                      />
                    </>
                  )}

                  <Spacer size="large" />
                </>
              )}

              <TextBody
                test-id="ps-channel-brand-moment-url-label"
                size="small"
              >
                <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.brand-moment-url">
                  Brand Moment URL
                </TranslationComponent>
              </TextBody>
              <Spacer size="2xsmall" />
              <Input
                test-id={
                  isUpdating
                    ? 'ps-update-channel-brand-moment-url-input'
                    : 'ps-create-channel-brand-moment-url-input'
                }
                variant="outlined"
                placeholder={
                  t(
                    'pro-studio/channel-details:form.placeholders.brand-moment-url'
                  ) || 'Brand Moment URL'
                }
                value={brandingForm.watch('brandMomentUrl')}
                {...brandingForm.register('brandMomentUrl')}
                // @ts-ignore
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { value } = e.target;
                  brandingForm.setValue('brandMomentUrl', value, {
                    shouldDirty: true,
                  });
                }}
                disabled={isViewer}
              />
              {!isViewer && (
                <>
                  <TextBody
                    test-id="ps-channel-brand-moment-url-or-label"
                    size="small"
                    textAlign="center"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.or">
                      or
                    </TranslationComponent>
                  </TextBody>
                  <Filepond
                    test-id={
                      isUpdating
                        ? 'ps-update-channel-brand-moment-url-filepond'
                        : 'ps-create-channel-brand-moment-url-filepond'
                    }
                    entityType={EntityTypes.ASSET}
                    entityId={botId}
                    readonly={isViewer}
                    // Removing a Brand Moment is not persisted yet (no backend
                    // delete — AUTO-4598 / AUTO-4599), so hide the remove
                    // affordance while keeping upload usable (PSTUDIO-3710).
                    hideRemove
                    onUpload={deferredBrandMomentUpload}
                    pictureSrc={brandingForm.watch('brandMomentUrl')}
                    onRemove={() => {
                      revokeBrandMomentPreview();
                      brandingForm.setValue('brandMomentFile', null, {
                        shouldDirty: true,
                      });
                      brandingForm.setValue('brandMomentUrl', '', {
                        shouldDirty: true,
                      });
                    }}
                    onChange={(p) => {
                      const file = p?.files?.[0];
                      if (!(file instanceof File)) {
                        return;
                      }
                      revokeBrandMomentPreview();
                      const previewUrl = URL.createObjectURL(file);
                      brandMomentPreviewUrlRef.current = previewUrl;
                      // Hold the raw File for the deferred Save-time PUT; the
                      // object URL drives the Filepond avatar + PypePreview
                      // until Save (PSTUDIO-3710).
                      brandingForm.setValue('brandMomentFile', file, {
                        shouldDirty: true,
                      });
                      brandingForm.setValue('brandMomentUrl', previewUrl, {
                        shouldDirty: true,
                      });
                    }}
                  />
                  <Spacer />
                  <Divider />
                </>
              )}
              <Spacer />

              <Grid>
                <GridCell xsmall="12" large="12">
                  <TextBody test-id="ps-channel-brand-color-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.brand-color">
                      Brand Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-brand-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.brand-color'
                      ) || 'Brand Color'
                    }
                    value={brandingForm.watch('widgetStyleColor')}
                    {...brandingForm.register('widgetStyleColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('widgetStyleColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-solution-bubble-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.solution-bubble-color">
                      Solution Bubble Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-solution-buble-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.solution-bubble-color'
                      ) || 'Solution Bubble Color'
                    }
                    value={brandingForm.watch('agentBubbleColor')}
                    {...brandingForm.register('agentBubbleColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('agentBubbleColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-solution-text-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.solution-text-color">
                      Solution Text Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-solution-buble-text-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.solution-text-color'
                      ) || 'Solution Text Color'
                    }
                    value={brandingForm.watch('agentBubbleTextColor')}
                    {...brandingForm.register('agentBubbleTextColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('agentBubbleTextColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-user-bubble-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.user-bubble-color">
                      User Bubble Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-user-buble-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.user-bubble-color'
                      ) || 'User Bubble Color'
                    }
                    value={brandingForm.watch('userBubbleColor')}
                    {...brandingForm.register('userBubbleColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('userBubbleColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-user-text-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.user-text-color">
                      User Text Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-user-buble-text-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.user-text-color'
                      ) || 'User Text Color'
                    }
                    value={brandingForm.watch('userBubbleTextColor')}
                    {...brandingForm.register('userBubbleTextColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('userBubbleTextColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-button-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.button-color">
                      Button Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-button-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.button-color'
                      ) || 'Button Color'
                    }
                    value={brandingForm.watch('buttonColor')}
                    {...brandingForm.register('buttonColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('buttonColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-button-text-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.button-text-color">
                      Button Text Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-button-text-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.button-text-color'
                      ) || 'Button Text Color'
                    }
                    value={brandingForm.watch('textColor')}
                    {...brandingForm.register('textColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('textColor', value, {
                        shouldDirty: true,
                      });
                    }}
                    disabled={isViewer}
                  />
                </GridCell>
              </Grid>
            </form>

            <Spacer size="xlarge" />
            <Divider />
            <Spacer size="xlarge" />

            <form>
              <Stack alignItems="center" gutter="small">
                <TextSubtitle test-id="ps-sizing-options-title">
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.sizing-options">
                    Sizing options
                  </TranslationComponent>
                </TextSubtitle>
                <Button
                  test-id="ps-sizing-options-reset-button"
                  size="small"
                  disabled={!sizingTouched || isViewer}
                  variant="ghost"
                  onClick={() => sizingForm.reset()}
                >
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.buttons.reset">
                    Reset
                  </TranslationComponent>
                </Button>
              </Stack>
              <Spacer />
              <Stack alignItems="end" gutter="small" nowrap>
                <div style={{ flexGrow: 1 }}>
                  <TextBody test-id="ps-channel-width-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.width">
                      Width
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <Input
                    test-id={
                      isUpdating
                        ? 'ps-update-channel-width-input'
                        : 'ps-create-channel-width-input'
                    }
                    placeholder={
                      t('pro-studio/channel-details:form.placeholders.width') ||
                      'Width'
                    }
                    value={`${sizingForm.watch('widgetWidth')}`}
                    variant="outlined"
                    suffix={
                      t(
                        'pro-studio/channel-details:form.placeholders.pixels'
                      ) || 'Pixels'
                    }
                    {...sizingForm.register('widgetWidth')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const {
                        target: { value },
                      } = e;
                      const ratioLocked =
                        sizingForm.getValues('shouldLockRatio');
                      const width = value ? parseInt(value) : 0;
                      sizingForm.setValue('widgetWidth', width, {
                        shouldDirty: true,
                      });

                      if (ratioLocked) {
                        const height = width
                          ? Math.round(width / widthToHeightRatio)
                          : 0;
                        sizingForm.setValue('widgetHeight', height, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    disabled={isViewer}
                  />
                </div>
                <IconButton
                  test-id="ps-sizing-options-lock-button"
                  size="large"
                  rounded
                  selected={sizingForm.watch('shouldLockRatio')}
                  name={sizingForm.watch('shouldLockRatio') ? 'lock' : 'unlock'}
                  disabled={isViewer}
                  title={
                    sizingForm.watch('shouldLockRatio') ? 'Unlock' : 'Lock'
                  }
                  onClick={() => {
                    sizingForm.setValue(
                      'shouldLockRatio',
                      !sizingForm.watch('shouldLockRatio'),
                      { shouldDirty: true }
                    );
                    setWidthToHeightRatio(
                      getWidthToHeightRatio(
                        +sizingForm.getValues('widgetWidth'),
                        +sizingForm.getValues('widgetHeight')
                      )
                    );
                  }}
                />
                <div style={{ flexGrow: 1 }}>
                  <TextBody test-id="ps-channel-height-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.height">
                      Height
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <Input
                    test-id={
                      isUpdating
                        ? 'ps-update-channel-height-input'
                        : 'ps-create-channel-height-input'
                    }
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.height'
                      ) || 'Height'
                    }
                    value={`${sizingForm.watch('widgetHeight')}`}
                    variant="outlined"
                    suffix={
                      t(
                        'pro-studio/channel-details:form.placeholders.pixels'
                      ) || 'Pixels'
                    }
                    {...sizingForm.register('widgetHeight')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const {
                        target: { value },
                      } = e;
                      const ratioLocked =
                        sizingForm.getValues('shouldLockRatio');
                      const height = value ? parseInt(value) : 0;
                      sizingForm.setValue('widgetHeight', height, {
                        shouldDirty: true,
                      });

                      if (ratioLocked) {
                        const width = Math.round(height * widthToHeightRatio);
                        sizingForm.setValue('widgetWidth', width, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    disabled={isViewer}
                  />
                </div>
              </Stack>
            </form>
          </PageSection>
        </GridCell>
        <GridCell xsmall="12" large="6">
          {/* CSS sticky, not `react-stickynode`: this column now lives in a tab
              panel that starts hidden (`display: none`), and stickynode measures
              on mount — it would latch onto a zero height and never recompute
              when the tab is first shown. `position: sticky` needs no
              measurement, and the flex GridCell stretches to the row height,
              which reproduces the old `bottomBoundary` exactly (PSTUDIO-3768). */}
          <div style={{ position: 'sticky', top: 92 }}>
            <PageSection size="small">
              <TextSubtitle test-id="ps-preview-title">
                <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.preview">
                  Preview
                </TranslationComponent>
              </TextSubtitle>
              <Spacer />
              <PypePreview
                logo={brandingForm.watch('logo')}
                brandMomentUrl={brandingForm.getValues('brandMomentUrl')}
                widgetStyleColor={brandingForm.getValues('widgetStyleColor')}
                widgetStyleTextColor={brandingForm.getValues(
                  'widgetStyleTextColor'
                )}
                agentBubbleColor={brandingForm.getValues('agentBubbleColor')}
                agentBubbleTextColor={brandingForm.getValues(
                  'agentBubbleTextColor'
                )}
                userBubbleColor={brandingForm.getValues('userBubbleColor')}
                userBubbleTextColor={brandingForm.getValues(
                  'userBubbleTextColor'
                )}
                buttonColor={brandingForm.getValues('buttonColor')}
                buttonTextColor={brandingForm.getValues('textColor')}
              />
              <Spacer size="xsmall" />
              <TextBody
                test-id="ps-preview-help-text"
                size="small"
                textAlign="center"
                variant="tertiary"
              >
                <TranslationComponent i18nKey="pro-studio/channel-details:form.help-texts.preview">
                  The position and size of the channel are not reflected in this
                  preview.
                </TranslationComponent>
              </TextBody>
            </PageSection>
          </div>
        </GridCell>
      </Grid>
    </>
  );

  // The "Settings" tab: the blocks that read/attach to the channel's pype, which
  // only exists once the channel is created — so they're edit-only, and so the
  // create page renders no tab bar at all.
  const settingsTabContent = (
    <ChannelSettingsTemplateSections
      ruleSets={pypeRuleGroups ?? []}
      ruleSetsLoaded={ruleGroupsLoaded}
      ruleSetsLoadFailed={ruleGroupsFailed}
      selectedRuleSetId={effectiveRuleSetId}
      onRuleSetChange={setSelectedRuleSetId}
      platformConnections={platformConnections}
      platformConnectionsLoaded={liveAgentLoaded}
      platformConnectionsLoadFailed={liveAgentFailed}
      selectedConnectionId={selectedConnectionId}
      onSelectConnection={onSelectConnection}
      credentials={channelCredentials}
      isViewer={isViewer}
    />
  );

  return (
    <>
      {isUpdating && (
        <>
          <ChannelSettingsHeader
            name={detailsForm.watch('name')}
            env={values.detailsFormValues?.env || ''}
            onRename={openRenameModal}
            isViewer={isViewer}
          />
          {/* `2xsmall`, matching the designer route's gap: the tab bar must sit
              at the same height on every tab or it jumps when you switch
              (PSTUDIO-3768), and the height reclaimed here is what the Pype Next
              tab's preview stage gets. */}
          <Spacer size="2xsmall" />
        </>
      )}
      {isUpdating ? (
        // Both panels stay mounted (`ps-tab-panel` hides with `display: none`),
        // so react-hook-form registration and dirty state survive a tab switch
        // and the one Save below still covers both tabs.
        <ChannelSettingsTabs active={initialTab}>
          <TabPanel name="settings">{settingsTabContent}</TabPanel>
          <TabPanel name="pype-theme">{themeTabContent}</TabPanel>
        </ChannelSettingsTabs>
      ) : (
        themeTabContent
      )}
      <PortalDestination name="channel-settings-form-snackbar-destination" />
      <Snackbar
        test-id="ps-channel-settings-form-snackbar"
        id="channel-settings-form-snackbar-destination"
        open={saveSnackbar.open}
      >
        {saveSnackbar.message === 'saving'
          ? 'Saving changes\u2026'
          : 'There are unsaved changes'}
        <Button
          test-id="ps-cancel-channel-settings-btn"
          variant="secondary"
          size="large"
          onClick={onCancel}
          disabled={isSubmitting}
          slot="action"
        >
          Cancel
        </Button>
        <Button
          test-id="ps-save-channel-settings-btn"
          size="large"
          onClick={onSubmit}
          disabled={!touched || isSubmitting}
          loading={isSubmitting}
          slot="action"
        >
          Save Changes
        </Button>
      </Snackbar>
      {editChannelNameModal}
      {unsavedChangesModal}
    </>
  );
};
